import { createContextStore } from '@eliadmin/core/src/stores/context-store';
import { buildWhereParams, storeApiResources, type ApiWhere } from '@eliadmin/core/src/api/store-list-request';
import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';

// Define the JobportalCurriculumsFilter type
export type JobportalCurriculumsFilter = {
  search?: string;
  age_min?: string;
  age_max?: string;
  categories?: string[];
  min_salary?: string;
  max_salary?: string;
  employment_types?: string[];
  languages?: string[];
  driving_licenses?: string[];
  salary_types?: string[];
  places_of_work?: string[];
  regions?: string[];
  educations?: string[];
  topped_up_to?: string;
  show_in_index?: boolean;
  order?: 'asc' | 'desc';
  orderBy?: 'created_at' | 'salary' | 'distance';
};

// Define the store state
type JobportalCurriculumsStoreState = {
  filter: JobportalCurriculumsFilter;
  autoUpdateSearchParams: boolean;
  clearTrigger: boolean;
  salaryRanges?: {
    '1': { min: string; max: string };
    '2': { min: string; max: string };
    overall: { min: string; max: string };
  };
};

// Define the store actions
type JobportalCurriculumsStoreActions = {
  clearFilters: () => void;
  setFilter: (filter: Partial<JobportalCurriculumsFilter>) => void;
  setAutoUpdateSearchParams: (value: boolean) => void;
  applyFilter: (filter: JobportalCurriculumsFilter) => void;
};

// Create the context store
const [JobportalCurriculumsStoreProvider, useJobportalCurriculumsStore] = createContextStore<
  JobportalCurriculumsStoreState,
  JobportalCurriculumsStoreActions
>(
  {
    filter: {},
    autoUpdateSearchParams: true,
    clearTrigger: false,
  },
  set => ({
    clearFilters: () => {
      set(state => {
        state.filter = {};
        state.clearTrigger = !state.clearTrigger;
      });
    },
    setFilter: filter => {
      set(state => {
        state.filter = {
          ...state.filter,
          ...filter,
        };
      });
    },
    setAutoUpdateSearchParams: value => {
      set(state => {
        state.autoUpdateSearchParams = value;
      });
    },
    applyFilter: (filter: JobportalCurriculumsFilter) => {
      const pathname = window.location.pathname;
      const newSearchParams = new URLSearchParams();

      Object.entries(filter).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          newSearchParams.set(key, value.toString());
        }
      });

      const searchString = newSearchParams.toString();
      window.history.replaceState({}, '', `${pathname}${searchString ? `?${searchString}` : ''}`);
    },
  }),
);

// JobportalCurriculumsFilterParamsSync component
function JobportalCurriculumsFilterParamsSync() {
  const filter = useJobportalCurriculumsStore(state => state.filter);
  const autoUpdateSearchParams = useJobportalCurriculumsStore(
    state => state.autoUpdateSearchParams,
  );
  const pathname = usePathname();

  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Update URL parameters when the filter changes
  useEffect(() => {
    if (!isMounted) return;
    if (!autoUpdateSearchParams) return; // Check the switch

    const newSearchParams = new URLSearchParams();

    Object.entries(filter).forEach(([key, value]) => {
      if (typeof value === 'boolean') {
        if (value) {
          newSearchParams.set(key, value.toString());
        }
        return;
      }

      if (
        value !== undefined &&
        value !== null &&
        value !== '' &&
        (typeof value !== 'string' || value.length > 0) &&
        (!Array.isArray(value) || value.length > 0)
      ) {
        newSearchParams.set(key, value.toString());
      }
    });

    const searchString = newSearchParams.toString();
    window.history.replaceState({}, '', `${pathname}${searchString ? `?${searchString}` : ''}`);
  }, [filter, isMounted, pathname, autoUpdateSearchParams]);

  return null;
}

// Export the components
export {
  JobportalCurriculumsStoreProvider,
  useJobportalCurriculumsStore,
  JobportalCurriculumsFilterParamsSync,
};

function buildCurriculumsFilterQuery({
  age_min,
  age_max,
  min_salary,
  max_salary,
  employment_types,
  languages,
  topped_up_to,
  categories,
  show_in_index,
}: JobportalCurriculumsFilter) {
  const conditions: string[] = [];

  if (age_min) {
    conditions.push(`age_min=${age_min}`);
  }

  if (age_max) {
    conditions.push(`age_max=${age_max}`);
  }

  if (min_salary) {
    conditions.push(`salary_min=${min_salary}`);
  }

  if (max_salary) {
    conditions.push(`salary_max=${max_salary}`);
  }

  if (employment_types) {
    conditions.push(`employment_types IN [${employment_types.join(',')}]`);
  }

  if (languages) {
    conditions.push(`languages.id IN [${languages.join(',')}]`);
  }

  if (categories) {
    conditions.push(`categories.id IN [${categories.join(',')}]`);
  }

  if (topped_up_to !== undefined) {
    conditions.push(`topped_up_to >= '${topped_up_to}'`);
  }

  if (show_in_index !== undefined) {
    conditions.push('show_in_index = true');
  }

  return conditions.join(' AND ');
}

export const buildCurriculumsQuery = (params: JobportalCurriculumsFilter) => {
  // The contract has no age_min/age_max predicates. Preserve the complete legacy
  // branch for those inputs (and the existing salary-option behavior), without parsing text.
  if (!params.age_min && !params.age_max && !params.min_salary && !params.max_salary) {
    const where: ApiWhere<typeof storeApiResources.storefrontJobPortalCurriculumVitaes> = {};
    if (params.categories?.length) where.categoriesId = { in: params.categories };
    if (params.employment_types?.length) where.employmentTypes = { in: params.employment_types };
    if (params.languages?.length) where.languagesId = { in: params.languages };
    if (params.topped_up_to) where.toppedUpTo = { greater_than_equal: params.topped_up_to };
    if (params.show_in_index) where.showInIndex = { equals: true };
    return {
      // Callers spread this after URL query params; preserve the previous filter overwrite.
      filter: undefined,
      ...(Object.keys(where).length ? buildWhereParams(where, storeApiResources.storefrontJobPortalCurriculumVitaes) : {}),
    };
  }
  const result: any = {
    filter: {},
  };

  if (params.age_min) {
    result.filter.age_min = params.age_min;
  }

  if (params.age_max) {
    result.filter.age_max = params.age_max;
  }

  if (params.min_salary) {
    result.filter.salary_min = params.max_salary;
  }

  if (params.min_salary) {
    result.filter.salary_max = params.max_salary;
  }

  if (params.categories && params.categories.length > 0) {
    result.filter.categories = params.categories;
  }

  if (params.employment_types && params.employment_types.length > 0) {
    result.filter.employment_types = params.employment_types;
  }

  if (params.languages && params.languages.length > 0) {
    result.filter.languages = params.languages;
  }

  if (params.topped_up_to) {
    result.filter.topped_up_to = params.topped_up_to;
  }

  if (params.show_in_index) {
    result.filter.show_in_index = params.show_in_index;
  }

  result.filter = buildCurriculumsFilterQuery(result.filter);

  return result;
};
